import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Wand2, Search, Sparkles, Check, X, AlertTriangle, CheckCheck } from 'lucide-react';
import AppShell from '../components/AppShell';
import { Button } from '../components/ui/button';
import axiosInstance from '../utils/axiosInstance';

const CATEGORIES = [
  '',
  'order_status',
  'tracking_issue',
  'missing_confirmation',
  'product_question',
  'international_shipping',
  'b2b_vat',
  'damaged_on_arrival',
  'refund_cancel',
  'wrong_item_return',
  'customs_duties',
  'invoice_request',
  'warranty_defective',
  'noise_outgoing',
];

const Macros = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [proposing, setProposing] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [filter, setFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showGapsOnly, setShowGapsOnly] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('api/replai/macros/');
      setItems(data.results || data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      await axiosInstance.post('api/replai/macros/sync/');
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setSyncing(false);
    }
  };

  const propose = async () => {
    setProposing(true);
    try {
      await axiosInstance.post('api/replai/macros/propose/', { only_unmapped: true });
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setProposing(false);
    }
  };

  const acceptAllProposals = async () => {
    const ok = window.confirm(
      'Accept all macro proposals with ≥80% confidence? ' +
      "This only updates Replai's own category field - no Gorgias writes."
    );
    if (!ok) return;
    setAccepting(true);
    try {
      const { data } = await axiosInstance.post('api/replai/macros/accept-proposals/', {
        min_confidence: 0.8,
      });
      await load();
      window.alert(
        `Accepted ${data.accepted} proposal${data.accepted === 1 ? '' : 's'}. ` +
        `${data.still_unmapped} macro${data.still_unmapped === 1 ? '' : 's'} still unmapped.`
      );
    } catch (e) {
      console.error(e);
    } finally {
      setAccepting(false);
    }
  };

  const update = async (id, patch) => {
    setItems((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
    try {
      await axiosInstance.patch(`api/replai/macros/${id}/`, patch);
    } catch (e) {
      console.error(e);
      await load();
    }
  };

  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const mapped = items.filter((m) => m.category).length;
    const gaps = items.filter((m) => !m.category && m.proposed_category === 'unknown').length;
    const suggested = items.filter((m) => !m.category && m.proposed_category && m.proposed_category !== 'unknown').length;
    return { total: items.length, mapped, gaps, suggested };
  }, [items]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return items.filter((m) => {
      if (showGapsOnly && !(m.proposed_category === 'unknown' && !m.category)) return false;
      if (categoryFilter && m.category !== categoryFilter) return false;
      if (!q) return true;
      return (
        (m.name || '').toLowerCase().includes(q) ||
        (m.body_text || '').toLowerCase().includes(q)
      );
    });
  }, [items, filter, categoryFilter, showGapsOnly]);

  const header = (
    <div className="flex items-center gap-2">
      <Button onClick={propose} disabled={proposing} variant="outline" className="gap-2">
        <Sparkles className={`h-4 w-4 ${proposing ? 'animate-pulse' : ''}`} />
        {proposing ? 'Proposing…' : 'Propose categories'}
      </Button>
      <Button onClick={acceptAllProposals} disabled={accepting} variant="outline" className="gap-2">
        <CheckCheck className={`h-4 w-4 ${accepting ? 'animate-pulse' : ''}`} />
        {accepting ? 'Accepting…' : 'Accept ≥80%'}
      </Button>
      <Button onClick={sync} disabled={syncing} className="gap-2">
        <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
        {syncing ? 'Syncing…' : 'Sync from Gorgias'}
      </Button>
    </div>
  );

  return (
    <AppShell
      title="Macros"
      subtitle="Gorgias canned replies mirrored into Replai. Map them to categories so the dispatcher can suggest them."
      headerRight={header}
    >
      <div className="grid grid-cols-4 gap-4 mb-6">
        <StatCard label="Total" value={counts.total} tone="neutral" />
        <StatCard label="Mapped" value={counts.mapped} tone="success" />
        <StatCard label="Needs review" value={counts.suggested} tone="warning" />
        <StatCard
          label="Gaps (no category fits)"
          value={counts.gaps}
          tone="danger"
          onClick={() => setShowGapsOnly((v) => !v)}
          active={showGapsOnly}
        />
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by name or body…"
            className="h-10 w-full rounded-xl border border-border bg-white pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="h-10 rounded-xl border border-border bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
        >
          <option value="">All categories</option>
          {CATEGORIES.filter((c) => c).map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-border/70 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Wand2 className="h-4 w-4" />
            {filtered.length} macro{filtered.length === 1 ? '' : 's'}
          </div>
          {loading && <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground bg-[hsl(var(--secondary))]">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3 w-48">Category</th>
              <th className="px-4 py-3 w-64">Keywords</th>
              <th className="px-4 py-3 w-24">Active</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/70">
            {filtered.map((m) => (
              <MacroRow key={m.id} macro={m} onUpdate={update} />
            ))}
            {filtered.length === 0 && !loading && (
              <tr>
                <td colSpan={4} className="px-4 py-10 text-center text-sm text-muted-foreground">
                  No macros match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
};

const MacroRow = ({ macro, onUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const [keywordInput, setKeywordInput] = useState((macro.keywords || []).join(', '));

  const saveKeywords = () => {
    const next = keywordInput
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (JSON.stringify(next) !== JSON.stringify(macro.keywords || [])) {
      onUpdate(macro.id, { keywords: next });
    }
  };

  const hasProposal =
    !macro.category && macro.proposed_category && macro.proposed_category !== 'unknown';
  const isGap = !macro.category && macro.proposed_category === 'unknown';

  const acceptProposal = () => onUpdate(macro.id, { category: macro.proposed_category });
  const rejectProposal = () =>
    onUpdate(macro.id, { proposed_category: '', proposal_confidence: 0, proposal_reason: '' });

  return (
    <>
      <tr className="hover:bg-[hsl(var(--secondary)/0.4)]">
        <td className="px-4 py-3">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="font-medium text-foreground text-left hover:underline"
          >
            {macro.name}
          </button>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {macro.language && (
              <span className="text-[0.6rem] text-muted-foreground uppercase">{macro.language}</span>
            )}
            {macro.usage > 0 && (
              <span className="text-[0.6rem] text-muted-foreground">used {macro.usage}×</span>
            )}
          </div>
          {isGap && macro.proposal_reason && (
            <p className="text-[0.65rem] text-rose-700 mt-1 flex items-start gap-1">
              <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
              <span>{macro.proposal_reason}</span>
            </p>
          )}
        </td>
        <td className="px-4 py-3">
          <select
            value={macro.category || ''}
            onChange={(e) => onUpdate(macro.id, { category: e.target.value })}
            className="h-8 w-full rounded-lg border border-border bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
          >
            <option value="">- unmapped -</option>
            {CATEGORIES.filter((c) => c).map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          {hasProposal && (
            <div className="mt-1.5 flex items-center gap-1.5 text-[0.65rem]">
              <span className="text-muted-foreground">suggested:</span>
              <span className="font-medium text-foreground">{macro.proposed_category}</span>
              <span className="text-muted-foreground">
                ({(macro.proposal_confidence * 100).toFixed(0)}%)
              </span>
              <button
                type="button"
                onClick={acceptProposal}
                className="ml-auto rounded p-0.5 text-emerald-600 hover:bg-emerald-50"
                title="Accept"
              >
                <Check className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={rejectProposal}
                className="rounded p-0.5 text-rose-600 hover:bg-rose-50"
                title="Dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </td>
        <td className="px-4 py-3">
          <input
            type="text"
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            onBlur={saveKeywords}
            placeholder="comma, separated, keywords"
            className="h-8 w-full rounded-lg border border-border bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
          />
        </td>
        <td className="px-4 py-3">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={macro.active}
              onChange={(e) => onUpdate(macro.id, { active: e.target.checked })}
              className="h-4 w-4 rounded border-border text-[hsl(var(--primary))] focus:ring-[hsl(var(--ring))]"
            />
            <span className="text-xs text-muted-foreground">{macro.active ? 'on' : 'off'}</span>
          </label>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-[hsl(var(--secondary)/0.4)]">
          <td colSpan={4} className="px-4 py-4">
            <pre className="text-xs text-foreground whitespace-pre-wrap font-mono leading-relaxed">
              {macro.body_text || '(empty body)'}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
};

const StatCard = ({ label, value, tone, onClick, active }) => {
  const map = {
    neutral: 'bg-white border-border',
    success: 'bg-emerald-50/60 border-emerald-200/70',
    warning: 'bg-amber-50/60 border-amber-200/70',
    danger: 'bg-rose-50/60 border-rose-200/70',
  };
  const clickable = Boolean(onClick);
  return (
    <div
      onClick={onClick}
      className={`rounded-2xl border ${map[tone]} p-4 transition ${
        clickable ? 'cursor-pointer hover:shadow-sm' : ''
      } ${active ? 'ring-2 ring-[hsl(var(--ring))]' : ''}`}
    >
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-display text-3xl font-semibold mt-1">{value}</p>
    </div>
  );
};

export default Macros;
